import React from 'react';
import PropTypes from 'prop-types';
import { NULL_VALUE } from 'components/internal/shared';
import { valueIf } from 'utilities/object';
import * as routes from 'utilities/routes';
import {
  toDate,
  toDateTime,
} from 'utilities/string';

export const recordProps = PropTypes.objectOf(
  PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string,
      ]),
    ),
    PropTypes.bool,
    PropTypes.number,
    PropTypes.shape({}),
    PropTypes.string,
  ]),
);

export const resultProps = PropTypes.shape({
  description: PropTypes.string,
  detailsUrl: PropTypes.string,
  id: PropTypes.number.isRequired,
  name: PropTypes.string.isRequired,
  recordUrl: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
});

export const rolesProps = PropTypes.objectOf(PropTypes.bool);

function getNullDimension() {
  return {
    label: 'NULL',
    value: NULL_VALUE,
  };
}

const pathsByType = {
  Ad: routes.internalExplorerAdPath,
  AdAccount: routes.internalExplorerAdAccountPath,
  AdCampaign: routes.internalExplorerAdCampaignPath,
  AdFormat: routes.internalExplorerAdFormatPath,
  Asset: routes.internalExplorerAssetPath,
  Audit: routes.internalExplorerAuditPath,
  AuditAsset: routes.internalExplorerAuditAssetPath,
  AuditPost: routes.internalExplorerAuditPostPath,
  Channel: routes.internalExplorerChannelPath,
  Company: routes.internalExplorerCompanyPath,
  FacebookAdAccount: routes.internalExplorerFacebookAdAccountPath,
  FacebookAdCampaign: routes.internalExplorerFacebookAdCampaignPath,
  FacebookAdCreative: routes.internalExplorerFacebookAdCreativePath,
  FacebookAdGroup: routes.internalExplorerFacebookAdGroupPath,
  Guideline: routes.internalExplorerGuidelinePath,
  GuidelineDetail: routes.internalExplorerGuidelineDetailPath,
  Market: routes.internalExplorerMarketPath,
  OrganizationBrand: routes.internalExplorerOrganizationBrandPath,
  Publisher: routes.internalExplorerPublisherPath,
  User: routes.internalExplorerUserPath,
};

const typesByIdKey = {
  ad_account_id: 'AdAccount',
  ad_campaign_id: 'AdCampaign',
  ad_format_id: 'AdFormat',
  asset_id: 'Asset',
  audit_asset_id: 'AuditAsset',
  audit_id: 'Audit',
  channel_id: 'Channel',
  company_id: 'Company',
  facebook_ad_account_id: 'FacebookAdAccount',
  facebook_ad_campaign_id: 'FacebookAdCampaign',
  guideline_detail_id: 'GuidelineDetail',
  guideline_id: 'Guideline',
  inviter_id: 'User',
  market_id: 'Market',
  organization_brand_id: 'OrganizationBrand',
  organization_id: '',
  parent_id: 'Company',
  primary_asset_id: 'Asset',
  primary_audit_post_id: 'AuditPost',
  publisher_id: 'Publisher',
  reviewer_id: 'User',
  user_id: 'User',
};

function determineType(key, inputValue) {
  if (key.endsWith('_id')) return 'id';
  if (key.endsWith('_at')) return 'time';
  if (key.endsWith('_date')) return 'date';
  if (inputValue.startsWith?.('http')) return 'link';

  return null;
}

function getRecordPath(key, value, record) {
  if (key === 'auditable_id') {
    switch (record.auditable_type) {
      case 'Ad':
        return routes.internalExplorerAdPath;
      case 'FacebookAdCreative':
        return routes.internalExplorerFacebookAdCreativePath;
      case 'FacebookAdGroup':
        return routes.internalExplorerFacebookAdGroupPath;
      default:
        return null;
    }
  }

  return null;
}

function getIdPath(key, value, record) {
  const path = pathsByType[typesByIdKey[key]] ?? getRecordPath(key, value, record);

  return path?.(value);
}

function formatElement(key, value, type, record) {
  switch (type) {
    case 'id': {
      const path = getIdPath(key, value, record);
      return path ? <a href={path}>{ value }</a> : null;
    }
    case 'link':
      return <a href={value}>{ value }</a>;
    default:
      return null;
  }
}

function formatLabel(value, type) {
  switch (type) {
    case 'array':
      return `[${value}]`;
    case 'bool':
      return value ? 'true' : 'false';
    case 'date':
      return toDate(new Date(value), true);
    case 'time':
      return toDateTime(new Date(value));
    default:
      return null;
  }
}

function formatValue(value, type) {
  switch (type) {
    case 'array':
      return `[${value}]`;
    default:
      return value;
  }
}

export function getDimension(key, record) {
  const inputValue = record[key];

  if (inputValue === null) return getNullDimension();

  const type = determineType(key, inputValue);

  if (type) {
    const element = formatElement(key, inputValue, type, record);
    const label = formatLabel(inputValue, type);
    const value = formatValue(inputValue, type);

    return {
      ...valueIf(element, 'element', element),
      ...valueIf(label, 'label', label),
      value,
    };
  }

  return {
    value: JSON.stringify(inputValue),
  };
}

export function getRecordItem(record) {
  const keys = Object.keys(record).sort();

  return keys.reduce((obj, key) => ({
    ...obj,
    [key]: getDimension(key, record),
  }), {});
}

export function renderResult({
  id,
  description,
  path,
  type,
}) {
  return (
    <a
      key={path}
      className="u-flexRow u-gap-8 u-marginBottom-16"
      href={path}
    >
      <div>{ id }</div>
      <div>{ type }</div>
      <div>{ description && `(${description})` }</div>
    </a>
  );
}

export function getExplorerPath(type, id) {
  if (!id) return null;

  return pathsByType[type]?.(id);
}
